import SockJS from "sockjs-client";
import {config} from "@/config";
import Stomp from "webstomp-client";
import {get} from "@/services/api";

const state = {
    stompClient: null,
    socket: null,
    connectionError: false,
    sessionError: null,
    sessionCode: null,
    socketSessionId: null,
    accessToken: {
        token: null, // uuid-token-string
        expiresAt: null, // ISO 2025-08-31T14:30:00Z
        sessionCode: null // session-uuid-here
    },
    sessionInfo: {
        id: null,
        sessionCode: null,
        status: null,
        startedAt: null,
        duration: null,
        endedAt: null,
        testId: null,
        testName: null,
        participantCount: 0,
        participants: []
    },
    timeRemaining: {
        remainingMinutes: '0',
        remainingSeconds: '00',
        sessionCode: null
    },
    results: [],
    heartbeatInterval: null,
    // filled from the server so the activity form can show what "leave empty" gives you
    sessionDefaults: {
        submissionGraceSeconds: null,
        joinWindowMinutes: null
    }
}

const getters = {
    connectionError: state => state.connectionError,
    sessionError: state => state.sessionError,
    sessionInfo: state => state.sessionInfo,
    sessionStatus: state => state.sessionInfo.status,
    sessionCode: state => state.sessionCode,
    timeRemaining: state => state.timeRemaining,
    results: state => state.results,
    sessionDefaults: state => state.sessionDefaults
}

/* eslint-disable */
const actions = {

    async getAccessToken({commit}, sessionCode){
        try {
            const response = await get(`gamification/session/token/${sessionCode}`)
            commit('setAccessToken', response.data.content)
            state.sessionCode = sessionCode
            return response.data.content
        } catch (e) {
            console.log('error ', e)
        }
    },

    async getTestSessionDetails({commit}, sessionCode) {
        try {
            const response = await get(`gamification/session/by_code/${sessionCode}`)
            commit('setSessionInfo', response.data.content)
            return response.data.content
        } catch (e) {
            console.log('error test session info', e)
        }
    },

    async fetchSessionDefaults({commit}) {
        try {
            const response = await get('gamification/session/defaults')
            commit('setSessionDefaults', response.data.content)
            return response.data.content
        } catch (e) {
            console.log('error session defaults', e)
        }
    },

    async fetchResults({commit}, sessionCode) {
        if(!sessionCode) return
        try {
            const response = await get(`gamification/session/by_code/${sessionCode}/results`)
            commit('setResults', response.data.items)
            return response.data.items
        } catch (e) {
            console.log('error session results', e)
        }
    },

    setSocketConfig(){
        state.socket = new SockJS(`${config.serverUrl}socket`)
        state.stompClient = Stomp.over(state.socket)
    },

    startSession(){
        state.stompClient.send(`/app/session/${state.accessToken.sessionCode}/start`, state.accessToken.token, {})
    },

    endSession(){
        state.stompClient.send(`/app/session/${state.accessToken.sessionCode}/end`, state.accessToken.token, {})
    },

    setHeartBeatInterval({commit}, interval){
        if(interval === null){
            clearInterval(state.heartbeatInterval)
        }
        commit('setHeartBeatInterval', interval)
    },

    heartbeat(){
        console.log('Heartbeat...')
        state.stompClient.send(`/app/session/${state.accessToken.sessionCode}/heartbeat`, state.accessToken.token, {})
    },

    clearSessionError({commit}){
        commit('setSessionError', null)
    },

    setInitialTimeRemaining({commit}, {duration, sessionCode}){
        const totalSeconds = Math.floor(duration / 1000);
        const remainingMinutes = Math.floor(totalSeconds / 60);
        const rawSeconds = totalSeconds % 60;
        const remainingSeconds = rawSeconds.toString().padStart(2, '0');
        commit('setTimeRemaining', {
            sessionCode: sessionCode,
            remainingMinutes: remainingMinutes,
            remainingSeconds: remainingSeconds,
        })
    },

    connect({commit}) {
        if(state.stompClient && state.accessToken.sessionCode){
            state.stompClient.connect(
                {},
                () => {
                    commit('setConnectionError', false)
                    commit('setSocketSessionId')

                    // subscribing error channel
                    state.stompClient.subscribe(`/session/${state.accessToken.sessionCode}/error/${state.socketSessionId}`, (message)=>{
                        const sessionErrorResponse = JSON.parse(message.body)
                        commit('setSessionError', sessionErrorResponse)
                    })

                    // subscribing test session channel with info about participants
                    state.stompClient.subscribe(`/session/${state.accessToken.sessionCode}/admin`, (message) => {
                        const sessionInfoResponse = JSON.parse(message.body)
                        commit('setSessionInfo', sessionInfoResponse)
                    });

                    // subscribing time updates
                    state.stompClient.subscribe(`/session/${state.accessToken.sessionCode}/admin/time-remaining`, (message) => {
                        const _message = JSON.parse(message.body)
                        const timeRemainingResponse = {
                            ..._message,
                            remainingSeconds: _message.remainingSeconds === 0 ? '00' : _message.remainingSeconds
                        }
                        commit('setTimeRemaining', timeRemainingResponse)
                    });

                    // subscribing results of participants who already submitted
                    state.stompClient.subscribe(`/session/${state.accessToken.sessionCode}/admin/results`, (message) => {
                        commit('setResults', JSON.parse(message.body))
                    });

                    // join request with token
                    state.stompClient.send(`/app/session/${state.accessToken.sessionCode}/join`, state.accessToken.token, {})
                },
                (error) => {
                    console.error('Socket connection error:', error);
                    commit('setConnectionError', true)
                }
            );
        }
    },
    disconnect() {
        if(state.stompClient){
            try {
                state.stompClient.disconnect();
            } catch (e) {
                // socket may still be CONNECTING
            }
        }
    },
    clear({commit}){
        commit('setSessionInfo',  { id: null, sessionCode: null, status: null, startedAt: null, duration: null, endedAt: null, testId: null, testName: null, participantCount: 0, participants: []})
        commit('setAccessToken', { token: null, expiresAt: null, sessionCode: null})
        commit('setHeartBeatInterval', null)
        commit('setResults', [])
        state.stompClient = null
        state.socket = null
        state.connectionError = false
        state.sessionError = null
        state.sessionCode = null
        state.socketSessionId = null
    }
}

const mutations = {
    setAccessToken: (state, accessToken) => {
        state.accessToken.sessionCode = accessToken.sessionId
        state.accessToken.token = accessToken.token
        state.accessToken.expiresAt = accessToken.expiresAt
    },
    setSocketSessionId: (state) => {
        if (state.socket._transport && state.socket._transport.url) {
            const url = state.socket._transport.url;
            const parts = url.split('/');
            state.socketSessionId = parts[parts.length - 2]
        }
    },
    setTimeRemaining: (state, timeRemainingResponse) => (Object.assign(state.timeRemaining, timeRemainingResponse)),
    setSessionInfo: (state, sessionInfoResponse) => ( Object.assign(state.sessionInfo, sessionInfoResponse)),
    setConnectionError: (state, error) => (state.connectionError = error),
    setSessionError: (state, sessionErrorResponse) => {
        if(sessionErrorResponse){
            state.sessionError = sessionErrorResponse.code
        } else {
            state.sessionError = sessionErrorResponse
        }
    },
    setHeartBeatInterval: (state, interval) => (state.heartbeatInterval = interval),
    setResults: (state, results) => (state.results = results ?? []),
    setSessionDefaults: (state, defaults) => (state.sessionDefaults = defaults ?? {submissionGraceSeconds: null, joinWindowMinutes: null})
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}